/**
 * Phase 13–19 — Getrennte Dexie-Stores pro Aggregat.
 */

import { getElkheirDb } from './elkheirDb';
import { normalizeBusinessStorageId } from '../../utils/sharedCore';
import { purchaseLineEntityId } from '../../utils/purchaseLineEntityId';
import { AGGREGATE_TYPES, STOCK_DATA_ROOT_ENTITY_ID } from './domainAggregateStore';
import { hasDiscrepancyManagementContent } from '../../utils/resolveDiscrepancyForEcarts';
import { runEntityStoreWrite } from './entityStoreActivityState';

export const ENTITY_TABLES = {
  [AGGREGATE_TYPES.SALES]: 'entity_sales',
  [AGGREGATE_TYPES.PURCHASES]: 'entity_purchases',
  [AGGREGATE_TYPES.CUSTOMERS]: 'entity_customers',
  [AGGREGATE_TYPES.LOTS]: 'entity_lots',
  [AGGREGATE_TYPES.PRODUCTS]: 'entity_products',
  [AGGREGATE_TYPES.SUPPLIERS]: 'entity_suppliers',
  [AGGREGATE_TYPES.STOCK_DATA]: 'entity_stock_data',
  [AGGREGATE_TYPES.INVOICES]: 'entity_invoices',
  [AGGREGATE_TYPES.ORDERS]: 'entity_orders',
  [AGGREGATE_TYPES.EXPENSES]: 'entity_expenses',
  [AGGREGATE_TYPES.INVOICE_PAYMENTS]: 'entity_invoice_payments',
  [AGGREGATE_TYPES.SUPPLIER_DEBTS]: 'entity_supplier_debts',
  [AGGREGATE_TYPES.CUSTOMER_DEBTS]: 'entity_customer_debts',
  [AGGREGATE_TYPES.CUSTOMER_INVOICES]: 'entity_customer_invoices',
  [AGGREGATE_TYPES.SUPERMARKET_PAYMENTS]: 'entity_supermarket_payments',
  [AGGREGATE_TYPES.SUPERMARKET_INVOICES]: 'entity_supermarket_invoices',
  [AGGREGATE_TYPES.EXPIRED_PRODUCTS]: 'entity_expired_products',
  [AGGREGATE_TYPES.NOTIFICATIONS]: 'entity_notifications',
  [AGGREGATE_TYPES.WORKER_SESSIONS]: 'entity_worker_sessions',
  [AGGREGATE_TYPES.REPORTS]: 'entity_reports',
  [AGGREGATE_TYPES.DISCREPANCY_MANAGEMENT]: 'entity_discrepancy_management',
  [AGGREGATE_TYPES.STOCK_LOCATIONS]: 'entity_stock_locations',
  [AGGREGATE_TYPES.PERSISTENT_STOCK_DATA]: 'entity_persistent_stock_data',
  [AGGREGATE_TYPES.OFFLINE_QUEUE]: 'entity_offline_queue',
  [AGGREGATE_TYPES.BUSINESS_INFO]: 'entity_business_info',
  [AGGREGATE_TYPES.USER_INFO]: 'entity_user_info',
  [AGGREGATE_TYPES.METADATA]: 'entity_metadata',
  [AGGREGATE_TYPES.STOCK_MOVEMENTS]: 'entity_stock_movements'
};

export const ENTITY_STORE_TABLE_NAMES = Object.values(ENTITY_TABLES);

/**
 * Resolve Dexie table names for payload/aggregate field names.
 * Used to narrow migration `'rw'` scope to only stores being synced.
 * @param {string[]} fields
 * @returns {string[]}
 */
export function getEntityTableNamesForFields(fields) {
  const names = new Set();
  for (const field of fields || []) {
    const table = ENTITY_TABLES[field];
    if (table) names.add(table);
  }
  return [...names];
}

/**
 * @param {import('dexie').Dexie} db
 * @param {string[]} fields
 * @returns {import('dexie').Table[]}
 */
export function getEntityTablesForFields(db, fields) {
  if (!db) return [];
  return getEntityTableNamesForFields(fields)
    .map((name) => db[name])
    .filter(Boolean);
}

export function isEntityStoreEnabled() {
  const v = process.env.REACT_APP_ENTITY_STORE;
  return !(v === 'false' || v === '0');
}

export function isEntityStoreReadEnabled() {
  if (!isEntityStoreEnabled()) return false;
  const v = process.env.REACT_APP_ENTITY_STORE_READ;
  return !(v === 'false' || v === '0');
}

export function isEntityStoreAuthorityEnabled() {
  if (!isEntityStoreEnabled()) return false;
  const v = process.env.REACT_APP_ENTITY_STORE_AUTHORITY;
  return !(v === 'false' || v === '0');
}

export function isEntityStorePrimaryReadEnabled() {
  if (!isEntityStoreReadEnabled()) return false;
  const v = process.env.REACT_APP_ENTITY_STORE_PRIMARY;
  if (v === 'false' || v === '0') return false;
  if (v === 'true' || v === '1') return true;
  return isEntityStoreAuthorityEnabled();
}

function entityRowId(businessId, aggregate, entityId) {
  return `${businessId}:${aggregate}:${entityId}`;
}

function idOf(obj, ...keys) {
  for (const key of keys) {
    const v = obj?.[key];
    if (v != null && v !== '') return String(v);
  }
  return '';
}

const entityIdFns = {
  [AGGREGATE_TYPES.SALES]: (s) => idOf(s, 'id', 'orderId'),
  [AGGREGATE_TYPES.PURCHASES]: (p) => purchaseLineEntityId(p),
  [AGGREGATE_TYPES.CUSTOMERS]: (c) => idOf(c, 'customerId', 'id'),
  [AGGREGATE_TYPES.LOTS]: (l) => idOf(l, 'id'),
  [AGGREGATE_TYPES.PRODUCTS]: (p) => idOf(p, 'id', 'productId'),
  [AGGREGATE_TYPES.SUPPLIERS]: (s) => idOf(s, 'id', 'supplierId'),
  [AGGREGATE_TYPES.INVOICES]: (i) => idOf(i, 'id', 'invoiceNumber'),
  [AGGREGATE_TYPES.ORDERS]: (o) => idOf(o, 'id', 'orderId'),
  [AGGREGATE_TYPES.EXPENSES]: (e) => idOf(e, 'id', 'expenseId'),
  [AGGREGATE_TYPES.INVOICE_PAYMENTS]: (p) => idOf(p, 'id', 'paymentId'),
  [AGGREGATE_TYPES.SUPPLIER_DEBTS]: (d) => idOf(d, 'id', 'supplierId'),
  [AGGREGATE_TYPES.CUSTOMER_DEBTS]: (d) => idOf(d, 'id', 'customerId'),
  [AGGREGATE_TYPES.CUSTOMER_INVOICES]: (i) => idOf(i, 'id', 'invoiceNumber'),
  [AGGREGATE_TYPES.SUPERMARKET_PAYMENTS]: (p) => idOf(p, 'id', 'paymentId'),
  [AGGREGATE_TYPES.SUPERMARKET_INVOICES]: (i) => idOf(i, 'id', 'invoiceNumber'),
  [AGGREGATE_TYPES.EXPIRED_PRODUCTS]: (e) => idOf(e, 'id', 'lotId'),
  [AGGREGATE_TYPES.NOTIFICATIONS]: (n) => idOf(n, 'id', 'notificationId'),
  [AGGREGATE_TYPES.WORKER_SESSIONS]: (s) => idOf(s, 'id', 'sessionId'),
  [AGGREGATE_TYPES.PERSISTENT_STOCK_DATA]: (i) => idOf(i, 'id', 'productId', 'locationId'),
  [AGGREGATE_TYPES.OFFLINE_QUEUE]: (i) => idOf(i, 'id', 'outboxId', 'timestamp'),
  [AGGREGATE_TYPES.STOCK_MOVEMENTS]: (m) => idOf(m, 'movementId', 'id')
};

const updatedAtFns = {
  [AGGREGATE_TYPES.SALES]: (s) => s.updatedAt || s.date,
  [AGGREGATE_TYPES.PURCHASES]: (p) => p.updatedAt || p.date,
  [AGGREGATE_TYPES.CUSTOMERS]: (c) => c.updatedAt,
  [AGGREGATE_TYPES.LOTS]: (l) => l.updatedAt,
  [AGGREGATE_TYPES.PRODUCTS]: (p) => p.updatedAt,
  [AGGREGATE_TYPES.SUPPLIERS]: (s) => s.updatedAt,
  [AGGREGATE_TYPES.INVOICES]: (i) => i.updatedAt || i.invoiceDate,
  [AGGREGATE_TYPES.ORDERS]: (o) => o.updatedAt || o.date,
  [AGGREGATE_TYPES.EXPENSES]: (e) => e.updatedAt || e.date,
  [AGGREGATE_TYPES.INVOICE_PAYMENTS]: (p) => p.updatedAt || p.date,
  [AGGREGATE_TYPES.SUPPLIER_DEBTS]: (d) => d.updatedAt,
  [AGGREGATE_TYPES.CUSTOMER_DEBTS]: (d) => d.updatedAt,
  [AGGREGATE_TYPES.CUSTOMER_INVOICES]: (i) => i.updatedAt || i.invoiceDate,
  [AGGREGATE_TYPES.SUPERMARKET_PAYMENTS]: (p) => p.updatedAt || p.date,
  [AGGREGATE_TYPES.SUPERMARKET_INVOICES]: (i) => i.updatedAt || i.invoiceDate,
  [AGGREGATE_TYPES.EXPIRED_PRODUCTS]: (e) => e.updatedAt || e.expiryDate,
  [AGGREGATE_TYPES.NOTIFICATIONS]: (n) => n.updatedAt || n.createdAt,
  [AGGREGATE_TYPES.WORKER_SESSIONS]: (s) => s.updatedAt || s.loginTime,
  [AGGREGATE_TYPES.PERSISTENT_STOCK_DATA]: (i) => i.updatedAt,
  [AGGREGATE_TYPES.OFFLINE_QUEUE]: (i) => i.updatedAt || i.timestamp,
  [AGGREGATE_TYPES.STOCK_MOVEMENTS]: (m) => m.updatedAt || m.createdAt
};

const ROOT_DOCUMENT_AGGREGATES = new Set([
  AGGREGATE_TYPES.STOCK_DATA,
  AGGREGATE_TYPES.REPORTS,
  AGGREGATE_TYPES.DISCREPANCY_MANAGEMENT,
  AGGREGATE_TYPES.BUSINESS_INFO,
  AGGREGATE_TYPES.USER_INFO,
  AGGREGATE_TYPES.METADATA
]);

const ARRAY_PAYLOAD_FIELDS = {
  [AGGREGATE_TYPES.SALES]: 'sales',
  [AGGREGATE_TYPES.PURCHASES]: 'purchases',
  [AGGREGATE_TYPES.CUSTOMERS]: 'customers',
  [AGGREGATE_TYPES.LOTS]: 'lots',
  [AGGREGATE_TYPES.PRODUCTS]: 'products',
  [AGGREGATE_TYPES.SUPPLIERS]: 'suppliers',
  [AGGREGATE_TYPES.INVOICES]: 'invoices',
  [AGGREGATE_TYPES.ORDERS]: 'orders',
  [AGGREGATE_TYPES.EXPENSES]: 'expenses',
  [AGGREGATE_TYPES.INVOICE_PAYMENTS]: 'invoicePayments',
  [AGGREGATE_TYPES.SUPPLIER_DEBTS]: 'supplierDebts',
  [AGGREGATE_TYPES.CUSTOMER_DEBTS]: 'customerDebts',
  [AGGREGATE_TYPES.CUSTOMER_INVOICES]: 'customerInvoices',
  [AGGREGATE_TYPES.SUPERMARKET_PAYMENTS]: 'supermarketPayments',
  [AGGREGATE_TYPES.SUPERMARKET_INVOICES]: 'supermarketInvoices',
  [AGGREGATE_TYPES.EXPIRED_PRODUCTS]: 'expiredProducts',
  [AGGREGATE_TYPES.NOTIFICATIONS]: 'notifications',
  [AGGREGATE_TYPES.WORKER_SESSIONS]: 'workerSessions',
  [AGGREGATE_TYPES.PERSISTENT_STOCK_DATA]: 'persistentStockData',
  [AGGREGATE_TYPES.OFFLINE_QUEUE]: 'offlineQueue',
  [AGGREGATE_TYPES.STOCK_MOVEMENTS]: 'stockMovements'
};

const ROOT_DOCUMENT_FIELDS = {
  [AGGREGATE_TYPES.STOCK_DATA]: 'stockData',
  [AGGREGATE_TYPES.REPORTS]: 'reports',
  [AGGREGATE_TYPES.DISCREPANCY_MANAGEMENT]: 'discrepancyManagement',
  [AGGREGATE_TYPES.BUSINESS_INFO]: 'businessInfo',
  [AGGREGATE_TYPES.USER_INFO]: 'userInfo',
  [AGGREGATE_TYPES.METADATA]: 'metadata'
};

function getEntityTable(db, aggregate) {
  const name = ENTITY_TABLES[aggregate];
  if (!name || !db[name]) return null;
  return db[name];
}

function hasRootDocumentContent(doc) {
  return doc && typeof doc === 'object' && Object.keys(doc).length > 0;
}

function normalizeArrayField(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return Object.values(raw);
  return [];
}

async function pruneEntityStore(db, businessId, aggregate, keepEntityIds) {
  const table = getEntityTable(db, aggregate);
  if (!table || !businessId) return;

  const bid = String(businessId);
  const rows = await table.where('businessId').equals(bid).toArray();
  const keep =
    keepEntityIds instanceof Set ? keepEntityIds : new Set(keepEntityIds);
  const staleIds = rows
    .filter((row) => !keep.has(String(row.entityId)))
    .map((row) => row.id);

  if (staleIds.length > 0) {
    await table.bulkDelete(staleIds);
  }
}

async function syncArrayAggregate(db, bid, fieldSet, aggregate, payload, rowsByTable) {
  if (!fieldSet.has(aggregate)) return;

  const field = ARRAY_PAYLOAD_FIELDS[aggregate];
  const raw = payload[field];
  const normalizeField =
    aggregate === AGGREGATE_TYPES.PURCHASES ||
    aggregate === AGGREGATE_TYPES.LOTS ||
    aggregate === AGGREGATE_TYPES.STOCK_MOVEMENTS;
  const items = normalizeField
    ? normalizeArrayField(raw)
    : Array.isArray(raw)
      ? raw
      : [];

  if (!normalizeField && !Array.isArray(raw)) {
    await pruneEntityStore(db, bid, aggregate, new Set());
    return;
  }

  if (items.length === 0 && raw == null) {
    await pruneEntityStore(db, bid, aggregate, new Set());
    return;
  }

  const idFn = entityIdFns[aggregate];
  const updatedFn = updatedAtFns[aggregate];
  const tableName = ENTITY_TABLES[aggregate];
  if (!rowsByTable.has(tableName)) rowsByTable.set(tableName, []);

  const keepIds = new Set();
  for (const item of items) {
    const entityId = idFn(item);
    if (!entityId) continue;
    keepIds.add(entityId);
    rowsByTable.get(tableName).push({
      id: entityRowId(bid, aggregate, entityId),
      businessId: bid,
      entityId,
      payload: item,
      updatedAt: updatedFn(item) || new Date().toISOString()
    });
  }
  await pruneEntityStore(db, bid, aggregate, keepIds);
}

async function syncStockLocationsRoot(db, bid, fieldSet, payload, rowsByTable) {
  if (!fieldSet.has(AGGREGATE_TYPES.STOCK_LOCATIONS)) return;

  const tableName = ENTITY_TABLES[AGGREGATE_TYPES.STOCK_LOCATIONS];
  const raw = payload.stockLocations;
  if (!Array.isArray(raw) || raw.length === 0) {
    await pruneEntityStore(db, bid, AGGREGATE_TYPES.STOCK_LOCATIONS, new Set());
    return;
  }

  if (!rowsByTable.has(tableName)) rowsByTable.set(tableName, []);
  rowsByTable.get(tableName).push({
    id: entityRowId(bid, AGGREGATE_TYPES.STOCK_LOCATIONS, STOCK_DATA_ROOT_ENTITY_ID),
    businessId: bid,
    entityId: STOCK_DATA_ROOT_ENTITY_ID,
    payload: { locations: raw },
    updatedAt: payload.payloadUpdatedAt || new Date().toISOString()
  });
  await pruneEntityStore(db, bid, AGGREGATE_TYPES.STOCK_LOCATIONS, new Set([
    STOCK_DATA_ROOT_ENTITY_ID
  ]));
}

async function syncRootDocumentAggregate(db, bid, fieldSet, aggregate, payload, rowsByTable) {
  if (!fieldSet.has(aggregate)) return;

  const field = ROOT_DOCUMENT_FIELDS[aggregate];
  const doc = payload[field];
  const tableName = ENTITY_TABLES[aggregate];

  const rootHasContent =
    aggregate === AGGREGATE_TYPES.DISCREPANCY_MANAGEMENT
      ? hasDiscrepancyManagementContent(doc)
      : hasRootDocumentContent(doc);

  if (!rootHasContent) {
    await pruneEntityStore(db, bid, aggregate, new Set());
    return;
  }

  if (!rowsByTable.has(tableName)) rowsByTable.set(tableName, []);
  rowsByTable.get(tableName).push({
    id: entityRowId(bid, aggregate, STOCK_DATA_ROOT_ENTITY_ID),
    businessId: bid,
    entityId: STOCK_DATA_ROOT_ENTITY_ID,
    payload: doc,
    updatedAt: payload.payloadUpdatedAt || new Date().toISOString()
  });
  await pruneEntityStore(db, bid, aggregate, new Set([STOCK_DATA_ROOT_ENTITY_ID]));
}

/**
 * @param {import('dexie').Dexie} db
 * @param {string} businessId
 * @param {object} payload
 * @param {string[]} [fields]
 */
export async function syncEntityStoresFromPayload(db, businessId, payload, fields) {
  if (!isEntityStoreEnabled() || !businessId || !payload) return;

  const bid = String(normalizeBusinessStorageId(businessId));
  const fieldSet = new Set(fields || Object.values(AGGREGATE_TYPES));
  const rowsByTable = new Map();

  for (const aggregate of Object.keys(ARRAY_PAYLOAD_FIELDS)) {
    await syncArrayAggregate(db, bid, fieldSet, aggregate, payload, rowsByTable);
  }

  for (const aggregate of ROOT_DOCUMENT_AGGREGATES) {
    await syncRootDocumentAggregate(db, bid, fieldSet, aggregate, payload, rowsByTable);
  }
  await syncStockLocationsRoot(db, bid, fieldSet, payload, rowsByTable);

  for (const [tableName, rows] of rowsByTable.entries()) {
    if (rows.length > 0 && db[tableName]) {
      await db[tableName].bulkPut(rows);
    }
  }
}

/**
 * Phase C — put one array-aggregate row without pruning siblings.
 * When `db` is passed, caller owns the Dexie transaction (no nested tx).
 *
 * @param {string} businessId
 * @param {string} aggregate  e.g. AGGREGATE_TYPES.PRODUCTS
 * @param {object} item
 * @param {import('dexie').Dexie|null} [db]
 * @returns {Promise<{ id: string, businessId: string, entityId: string, payload: object, updatedAt: string }|null>}
 */
export async function putEntityArrayItem(businessId, aggregate, item, db = null) {
  if (!isEntityStoreEnabled() || !businessId || !item) return null;

  const idFn = entityIdFns[aggregate];
  const updatedFn = updatedAtFns[aggregate];
  if (!idFn) {
    throw new Error(`putEntityArrayItem: unsupported aggregate ${aggregate}`);
  }

  const entityId = idFn(item);
  if (!entityId) {
    throw new Error('putEntityArrayItem: missing entityId');
  }

  const bid = String(normalizeBusinessStorageId(businessId));
  const row = {
    id: entityRowId(bid, aggregate, entityId),
    businessId: bid,
    entityId,
    payload: item,
    updatedAt: updatedFn?.(item) || new Date().toISOString()
  };

  if (db) {
    const table = getEntityTable(db, aggregate);
    if (!table) {
      throw new Error(`putEntityArrayItem: missing table for ${aggregate}`);
    }
    await table.put(row);
    return row;
  }

  const database = await ensureEntityStoreTablesReady();
  const table = getEntityTable(database, aggregate);
  if (!table) {
    throw new Error(`putEntityArrayItem: missing table for ${aggregate}`);
  }

  return runEntityStoreWrite(
    'put_entity_array_item',
    { storeCount: 1, fields: [aggregate] },
    async () => {
      await database.transaction('rw', table, async () => {
        await table.put(row);
      });
      return row;
    }
  );
}

/**
 * @param {string} businessId
 * @param {object} product
 * @param {import('dexie').Dexie|null} [db]
 */
export async function putEntityProduct(businessId, product, db = null) {
  return putEntityArrayItem(businessId, AGGREGATE_TYPES.PRODUCTS, product, db);
}

/**
 * @param {string} businessId
 * @param {object} customer
 * @param {import('dexie').Dexie|null} [db]
 */
export async function putEntityCustomer(businessId, customer, db = null) {
  return putEntityArrayItem(businessId, AGGREGATE_TYPES.CUSTOMERS, customer, db);
}

/**
 * @param {string} businessId
 * @param {object} lot
 * @param {import('dexie').Dexie|null} [db]
 */
export async function putEntityLot(businessId, lot, db = null) {
  return putEntityArrayItem(businessId, AGGREGATE_TYPES.LOTS, lot, db);
}

async function listEntityRecords(businessId, aggregate) {
  if (!businessId || !isEntityStoreEnabled()) return [];
  const db = getElkheirDb();
  const table = getEntityTable(db, aggregate);
  if (!table) return [];

  const bid = String(normalizeBusinessStorageId(businessId));
  const rows = await table.where('businessId').equals(bid).toArray();
  return rows.map((r) => r.payload).filter(Boolean);
}

async function getEntityRootDocument(businessId, aggregate) {
  if (!businessId || !isEntityStoreEnabled()) return {};
  const db = getElkheirDb();
  const table = getEntityTable(db, aggregate);
  if (!table) return {};

  const bid = String(normalizeBusinessStorageId(businessId));
  const row = await table.get(
    entityRowId(bid, aggregate, STOCK_DATA_ROOT_ENTITY_ID)
  );
  const doc = row?.payload;
  return doc && typeof doc === 'object' ? doc : {};
}

export async function listEntitySales(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.SALES);
}
export async function listEntityPurchases(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.PURCHASES);
}
export async function listEntityLots(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.LOTS);
}
export async function listEntityProducts(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.PRODUCTS);
}
export async function listEntityCustomers(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.CUSTOMERS);
}
export async function listEntitySuppliers(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.SUPPLIERS);
}
export async function listEntityInvoices(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.INVOICES);
}
export async function listEntityOrders(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.ORDERS);
}
export async function listEntityExpenses(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.EXPENSES);
}
export async function listEntityInvoicePayments(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.INVOICE_PAYMENTS);
}
export async function listEntitySupplierDebts(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.SUPPLIER_DEBTS);
}
export async function listEntityCustomerDebts(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.CUSTOMER_DEBTS);
}
export async function listEntityCustomerInvoices(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.CUSTOMER_INVOICES);
}
export async function listEntitySupermarketPayments(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.SUPERMARKET_PAYMENTS);
}
export async function listEntitySupermarketInvoices(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.SUPERMARKET_INVOICES);
}
export async function listEntityExpiredProducts(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.EXPIRED_PRODUCTS);
}
export async function listEntityNotifications(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.NOTIFICATIONS);
}
export async function listEntityWorkerSessions(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.WORKER_SESSIONS);
}

export async function getEntityStockData(businessId) {
  return getEntityRootDocument(businessId, AGGREGATE_TYPES.STOCK_DATA);
}
export async function getEntityReports(businessId) {
  return getEntityRootDocument(businessId, AGGREGATE_TYPES.REPORTS);
}
export async function getEntityDiscrepancyManagement(businessId) {
  return getEntityRootDocument(businessId, AGGREGATE_TYPES.DISCREPANCY_MANAGEMENT);
}
export async function listEntityPersistentStockData(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.PERSISTENT_STOCK_DATA);
}
export async function listEntityOfflineQueue(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.OFFLINE_QUEUE);
}
export async function listEntityStockMovements(businessId) {
  return listEntityRecords(businessId, AGGREGATE_TYPES.STOCK_MOVEMENTS);
}
export async function getEntityStockLocations(businessId) {
  const doc = await getEntityRootDocument(businessId, AGGREGATE_TYPES.STOCK_LOCATIONS);
  return Array.isArray(doc?.locations) ? doc.locations : [];
}
export async function getEntityBusinessInfo(businessId) {
  const doc = await getEntityRootDocument(businessId, AGGREGATE_TYPES.BUSINESS_INFO);
  return hasRootDocumentContent(doc) ? doc : null;
}
export async function getEntityUserInfo(businessId) {
  const doc = await getEntityRootDocument(businessId, AGGREGATE_TYPES.USER_INFO);
  return hasRootDocumentContent(doc) ? doc : null;
}
export async function getEntityMetadata(businessId) {
  return getEntityRootDocument(businessId, AGGREGATE_TYPES.METADATA);
}

export async function countEntityStoresByType(businessId) {
  if (!businessId || !isEntityStoreEnabled()) return {};
  const db = getElkheirDb();
  const bid = String(normalizeBusinessStorageId(businessId));
  const counts = {};
  for (const aggregate of Object.values(AGGREGATE_TYPES)) {
    const table = getEntityTable(db, aggregate);
    counts[aggregate] = table
      ? await table.where('businessId').equals(bid).count()
      : 0;
  }
  return counts;
}

export async function ensureEntityStoreTablesReady() {
  const db = getElkheirDb();
  if (!db.isOpen()) {
    await db.open();
  }
  const missing = ENTITY_STORE_TABLE_NAMES.filter((name) => !db[name]);
  if (missing.length > 0) {
    throw new Error(
      `Entity store tables missing (schema v10): ${missing.join(', ')}`
    );
  }
  return db;
}
